import { useMutation, useQuery, useQueryClient } from "react-query";
import { getInventory, transferStock, getTransferHistory, exportInventoryPdf, exportTransferHistoryPdf, deleteProduct, updateProductPrice, updateProductStock } from "../../../services";
import type { InventoryQueryParams, TransferPayload, UpdatePricePayload, UpdateStockPayload } from "../../../privatePages/Inventory/components/ProductsTable/types";
import { QUERY_KEYS } from "../queryKeys";
import { errorToast } from "../../../services/toasts";

const INVENTORY_PAGE_SIZE = 50;
const INVENTORY_STALE_TIME = 5 * 60 * 1000;

/**
 * Fetches one page of inventory for the authenticated supplier.
 * Returns products with per-location stock and the list of storage addresses.
 * GET /v2/inventory?search=...&limit=...&offset=...
 */
export const useGetInventory = (params?: InventoryQueryParams) => {
  const queryParams = {
    search: params?.search || undefined,
    limit: params?.limit ?? INVENTORY_PAGE_SIZE,
    offset: params?.offset ?? 0,
  };

  return useQuery({
    queryKey: [QUERY_KEYS.GET_INVENTORY, queryParams],
    queryFn: () => getInventory(queryParams),
    staleTime: INVENTORY_STALE_TIME,
    refetchOnWindowFocus: true,
  });
};

/**
 * Mutation to atomically transfer stock between two storage addresses.
 * POST /v2/inventory/transfer
 */
export const useTransferStock = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TransferPayload) => transferStock(payload),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_INVENTORY]);
    },
  });
};

/**
 * Fetches the transfer history for the authenticated supplier.
 * Optionally scoped to a single product with pagination.
 * GET /v2/inventory/transfer-history
 */
export const useGetTransferHistory = (productId?: number, page?: number, limit?: number) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_TRANSFER_HISTORY, productId ?? null, page ?? null, limit ?? null],
    queryFn: () => getTransferHistory(productId, page, limit),
    refetchOnWindowFocus: false,
    enabled: true,
  });
};

/**
 * Mutation to export inventory as PDF.
 * On success, opens the generated PDF URL in a new tab.
 * GET /v2/inventory/export
 */
export const useExportInventoryPdf = () => {
  return useMutation({
    mutationFn: () => exportInventoryPdf(),
    onSuccess: (data) => {
      window.open(data.url, "_blank");
    },
    onError: () => {
      errorToast("Error al exportar inventario");
    },
  });
};

/**
 * Mutation to export transfer history as PDF.
 * On success, opens the generated PDF URL in a new tab.
 * GET /v2/inventory/transfer-history/export
 */
export const useExportTransferHistoryPdf = () => {
  return useMutation({
    mutationFn: () => exportTransferHistoryPdf(),
    onSuccess: (data) => {
      window.open(data.url, "_blank");
    },
    onError: () => {
      errorToast("Error al exportar historial de traslados");
    },
  });
};

/**
 * Mutation to soft-delete a product.
 * On success, invalidates the inventory query to refresh the list.
 * DELETE /v2/supplier/products/:id
 */
export const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId: number) => deleteProduct(productId),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_INVENTORY]);
    },
  });
};

/**
 * Mutation to update a product's price (and taxed_price) inline from the
 * inventory table. Guard: backend requires inventory:edit_value permission.
 * On success, invalidates the inventory query to refresh the row.
 * PATCH /v2/inventory/products/:id/price
 */
export const useUpdateProductPrice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, payload }: { productId: number; payload: UpdatePricePayload }) =>
      updateProductPrice(productId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_INVENTORY]);
    },
  });
};

/**
 * Mutation to update a per-warehouse stock quantity inline from the inventory
 * table. Guard: backend requires inventory:edit_quantity permission.
 * On success, invalidates the inventory query to refresh the row + the
 * recomputed units_available total.
 * PATCH /v2/inventory/stock
 */
export const useUpdateProductStock = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateStockPayload) => updateProductStock(payload),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_INVENTORY]);
    },
  });
};
